/* eslint-disable max-lines-per-function */

import EventBus from '@mvps-genesis/event-bus';
import 'custom-extensions/ui-components/x-fans-like-card';
import GTM from 'extensions/gtm';
import {html, LitElement, nothing} from 'lit';
import Recommendations from '../../../Recommendations';
import stylesheet from './fans-also-like.component.scss?as=stylesheet';

/**
 * Constructor "often bought with" / "fans also liked" PDP recommendations grid.
 *
 * Always seeds the pod with the active PDP product id.
 *
 * Attributes:
 * - `pod-id` (required) — Constructor pod ID to fetch recommendations for (e.g. `often-bought-with`).
 * - `product-id` (required) — Miva product ID for the active PDP; the value Constructor indexes as `item_id`.
 * - `list` — GTM impression list name (default "Product Recommendations").
 * - `limit` — max products to render (default 10).
 * - `recommendation-type` — optional `PDP_FANS_ALSO_LIKE`, selects the DAM image transformation set used
 *   by the `constructor-recommendations` JSON API function. Leave empty to use the global product card
 *   transformation.
 */
export default class FansAlsoLikeRecommendations extends LitElement {

	static get properties () {
		return {
			_isLoading: {
				type: Boolean
			},
			_recommendations: {
				type: Array
			},
			limit: {
				type: Number
			},
			list: {
				type: String
			},
			podId: {
				attribute: 'pod-id',
				type: String
			},
			productId: {
				attribute: 'product-id',
				type: String
			},
			recommendationType: {
				attribute: 'recommendation-type',
				type: String
			}
		};
	};

	static get styles () {
		return [stylesheet];
	}

	get listSlug () {
		return this.slugify(this.list);
	}

	constructor () {
		super();
		this._onQuickAddToCart = this.onQuickAddToCart.bind(this);
		this._onResultClick = event => this.handleResultClick(event);

		// Create gtm helper
		this.gtm = new GTM(window.mivaJS);
		this._isLoading = true;
		this._recommendations = [];
		this._meta = null;
		this.list = 'Product Recommendations';
		this.limit = 10;
		this.podId = '';
		this.productId = '';
		this.recommendationType = '';
	}

	connectedCallback () {
		super.connectedCallback();

		// Listen for add to cart event from iframe and handle response
		EventBus.addEventListener('MVPS:addToCartQuickAdd', this._onQuickAddToCart, false);
	}

	/**
	 * Invoked when a component is removed from the document's DOM.
	 * @see https://lit.dev/docs/components/lifecycle/#disconnectedcallback
	 * @static
	 */
	disconnectedCallback () {
		super.disconnectedCallback();
		EventBus.removeEventListener('MVPS:addToCartQuickAdd', this._onQuickAddToCart, false);
	}

	/**
	 * Updates applicable product details for API calls when a variant has changed.
	 * @param {Event}	e
	 */
	onQuickAddToCart (e) {
		// On successful add to cart
		if (e.detail.success) {
			const productFormClicked = e.detail.form;
			const productCode = productFormClicked.dataset.productCode;
			const productCard = this.shadowRoot.getElementById(`js-fans-also-liked-${productCode}`);
			const productModal = productCard?.shadowRoot.querySelector('g-modal');

			/*
			 * Ignore if not this instance's card
			 * or it was never opened
			 */
			if (!productCard || !productModal?.isOpen) {
				return;
			}

			const productAddedButton = productCard.buttonRef.value;

			productModal.close();
			productCard.buttonDisabled = true;
			productAddedButton.classList.add('is-added');
			productAddedButton.innerHTML = '<i class="u-icon-check"></i> Added';
		}
	}

	sendImpressions () {
		if (!this._recommendations?.length) {
			return;
		}
		const gtmList = this.list;
		this.gtm.searchSendImpressions(this.gtm.slugify(gtmList), gtmList, this._recommendations);
	}

	async queueRecommendations () {
		if (!this.podId) {
			// eslint-disable-next-line no-console
			console.warn('<x-fans-also-like>: pod-id attribute is required');
			return;
		}

		if (!this.productId) {
			// eslint-disable-next-line no-console
			console.warn('<x-fans-also-like>: product-id attribute is required');
			return;
		}

		const {meta, products} = await Recommendations.get({
			limit: this.limit,
			list: this.listSlug,
			page: 'pdp',
			podId: this.podId,
			productId: this.productId,
			recommendationType: this.recommendationType
		});

		this._meta = meta;
		this._recommendations = products;
	}

	async firstUpdated () {
		try {
			await this.queueRecommendations();
		} finally {
			this._isLoading = false;
		}

		this.sendImpressions();

		Recommendations.trackView(this._meta, this._recommendations);
	}

	/**
	 * @param {Event} event
	 */
	handleResultClick (event) {
		const path = event.composedPath();

		if (path.some(node => [
			'BUTTON',
			'FORM',
			'INPUT',
			'SELECT'
		].includes(node?.tagName))) {
			return;
		}

		const card = path.find(node => node?.dataset?.cnstrcItemId);

		if (!card) {
			return;
		}

		const index = this._recommendations.findIndex(product => String(product?.code) === card.dataset.cnstrcItemId);

		if (index === -1) {
			return;
		}

		Recommendations.trackClick(this._meta, this._recommendations[index], index + 1);
	}

	renderProductResults () {
		const gtmData = {
			listId: this.gtm.slugify(this.list),
			listName: this.list
		};

		return this._recommendations?.map((product, index) => {
			const pos = index + 1;
			const dataLayer = this.gtmCustomProperties(product.customfields) || '';
			return html`
				<x-fans-like-card
					id="js-fans-also-liked-${product.code}"
					code="${product.code}"
					gtm-datalayer="${dataLayer}"
					gtm-list="${gtmData?.listName}"
					gtm-list-id="${gtmData?.listId}"
					gtm-position="${pos}"
					image-src-desktop="${product?.images?.main?.desktop}"
					image-src-mobile="${product?.images?.main?.mobile}"
					name="${product.name}"
					original-price="${product?.customfields?.original_price}"
					price="${product.price}"
					product-id="${product?.id}"
					style-number="${product?.sku}"
					url="${product.url}"
					data-cnstrc-item="recommendation"
					data-cnstrc-item-id="${product?.code}"
					data-cnstrc-item-name="${product.name}"
					data-cnstrc-item-price="${product.price}"
				></x-fans-like-card>
			`;
		});
	}

	gtmCustomProperties (datafields) {
		const props = window.mivaJS?.GTM_Custom_Properties;
		if (!props || !datafields) {
			return '{}';
		}

		const datalayer = datafields || {};

		const customObject = props.reduce((acc, key) => {

			const rawValue = datalayer[key];

			const normalized = this.normalizeValue(rawValue, key);

			if (typeof normalized === 'undefined') {
				return acc;
			}

			// Always keep original key
			acc[key] = normalized;

			const KEY_EXPANSIONS = {
				// eslint-disable-next-line camelcase
				featured_brand: [
					'brand',
					'item_brand'
				]
			};

			// Expand to additional keys if configured
			if (KEY_EXPANSIONS[key]) {
				KEY_EXPANSIONS[key].forEach(expandedKey => {
					acc[expandedKey] = normalized;
				});
			}

			return acc;
		}, {});

		return JSON.stringify(customObject);
	}

	normalizeValue (value, key) {
		const YES_NO_KEYS = new Set(['drop_ship_item']);

		const isEmpty =
			value === null ||
			value === '' ||
			typeof value === 'undefined';

		// Normalize yes/no semantics for any key
		if (value === 1 || value === '1') {
			return 'yes';
		}
		if (value === 0 || value === '0') {
			return 'no';
		}

		if (typeof value === 'string') {
			const lower = value.toLowerCase();
			if (lower === 'yes') {
				return 'yes';
			}
			if (lower === 'no') {
				return 'no';
			}
			if (lower === 'true') {
				return true;
			}
			if (lower === 'false') {
				return false;
			}
		}

		// Enforce yes/no for strict keys
		if (YES_NO_KEYS.has(key)) {
			return 'no';
		}

		// Generic empty handling
		if (isEmpty) {
			return void 0;
		}

		// Numeric strings → numbers
		const num = Number(value);
		if (Number.isFinite(num)) {
			return num;
		}

		return value;
	}

	renderSkeleton () {
		const cardCount = Math.max(1, Number(this.limit) || 2);

		return html`
			<div class="x-product-fans-also-liked__header x-type-subheading-xsmall">OFTEN BOUGHT WITH</div>
			<div class="x-product-fans-also-liked-products x-product-fans-also-liked-products--loading" aria-hidden="true">
				${Array.from({length: cardCount}).map(() => html`
					<div class="x-product-fans-also-liked__skeleton-row">
						<div class="x-product-fans-also-liked__skeleton-image"></div>
						<div class="x-product-fans-also-liked__skeleton-details">
							<div class="x-product-fans-also-liked__skeleton-line x-product-fans-also-liked__skeleton-line--name"></div>
							<div class="x-product-fans-also-liked__skeleton-line x-product-fans-also-liked__skeleton-line--price"></div>
						</div>
					</div>
				`)}
			</div>
		`;
	}

	render () {
		if (this._isLoading) {
			return this.renderSkeleton();
		}

		if (!this._recommendations?.length) {
			return nothing;
		}
		return html`
			<div class="x-product-fans-also-liked__header x-type-subheading-xsmall">OFTEN BOUGHT WITH</div>
			<div class="x-product-fans-also-liked-products js-gtm-list" @click="${this._onResultClick}" data-gtm-list="Fans Also Liked Products" data-cnstrc-recommendations data-cnstrc-recommendations-pod-id="${this.podId}" data-cnstrc-num-results="${this._recommendations.length}" data-cnstrc-recommendations-seed-items="${this.productId}">
				${this.renderProductResults()}
			</div>
		`;
	}

	// Helpers
	htmlEntities (str) {
		// eslint-disable-next-line newline-per-chained-call
		return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	/**
	 * Slugify a string
	 * @param {String} text
	 * @returns {void}
	 */
	slugify (text) {
		return text.toLowerCase().replace(/[^\w ,-]+/g, '').replace(/ +/g, '-');
	};
};

window.customElements.define('x-fans-also-like', FansAlsoLikeRecommendations);
